/* global cURL */
import Swal from 'sweetalert2';

export class ContactForm {
  #form;
  #debug;
  #subjectInputs;
  #subjectOtherInput;
  #submitButton;

  constructor(element, debug) {
    this.#form = element;
    this.#debug = debug;

    if (!this.#form.classList.contains('contact-form')) this.#form = this.#form.closest('.contact-form');

    if (!this.#form || !this.#form.classList.contains('contact-form')) return;

    this.#init();
  }

  #init() {
    if (this.#debug) console.log('initializing contact form...');

    this.#subjectInputs = this.#form.querySelectorAll('[name="subject"]');
    this.#subjectOtherInput = this.#form.querySelector('[name="subject-other"]');
    this.#submitButton = this.#form.querySelector('[type="submit"]');

    this.#toggleSubjectOther();
    this.#setEvents();
  }

  #setEvents() {
    this.#subjectInputs.forEach(input => {
      input.addEventListener('click', () => this.#toggleSubjectOther());
    });

    this.#submitButton.addEventListener('click', e => {
      e.preventDefault();
      this.#validate();
    });
  }

  #toggleSubjectOther() {
    const selected = this.#form.querySelector('[name="subject"]:checked');
    const showOther = selected?.value === '-99';
    const toggleTarget = this.#subjectOtherInput.closest('.fields-other__row') ?? this.#subjectOtherInput;
    toggleTarget.style.display = showOther ? 'flex' : 'none';
    if (!showOther) this.#subjectOtherInput.value = '';
  }

  #isEmail(val) {
    const atPosition = val.indexOf('@');
    const dotPosition = val.lastIndexOf('.');
    return atPosition > 0 && dotPosition > atPosition + 1 && dotPosition < val.length - 2;
  }

  #getCallbackMessage(status) {
    switch (status) {
      case 'success':
        return `Bedankt voor je bericht!<br>
                &nbsp;<br>
                We nemen zo snel mogelijk contact met je op.`;
      case 'missing':
        return 'Niet alle verplichte velden zijn ingevuld.';
      case 'invalid':
        return `Het gekozen onderwerp of de gekozen gemeente is niet (meer) beschikbaar.<br>
                Herlaad de pagina en probeer het opnieuw.`;
      case 'invalid_email':
        return 'Het opgegeven e-mailadres is ongeldig.';
      case 'spam':
        return `Het formulier werd veel sneller opgeslagen dan mogelijk zou moeten zijn.<br>
                Invoer wordt genegeerd op verdenking van spam.`;
      case 'error':
        return `Er is helaas iets misgegaan tijdens het verzenden van je bericht.<br>
                Mocht het probleem zich blijven voordoen neem dan contact op met een beheerder.`;
      case 'ajaxerror':
        return `Er is helaas iets misgegaan tijdens het verzenden van je bericht.<br>
                Mocht het probleem zich blijven voordoen neem dan contact op met een beheerder.<br>
                &nbsp;<br>
                Foutmelding: AJAX request mislukt.`;
      default:
        return `Onbekende fout (${status}).`;
    }
  }

  #validate() {
    if (this.#debug) console.log('validating...');

    const selectedSubject = this.#form.querySelector('[name="subject"]:checked');
    if (!selectedSubject) {
      if (this.#debug) console.log('>>> subject check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Selecteer een onderwerp.' });
      return;
    }

    if (selectedSubject.value === '-99') {
      const subjectOther = this.#subjectOtherInput.value.trim();
      if (!subjectOther) {
        if (this.#debug) console.log('>>> subject-other check FAILED! ending');
        Swal.fire({ icon: 'error', title: '', html: 'Vul in waarover je contact wil.' });
        return;
      }
    }

    const name = this.#form.querySelector('[name="name"]')?.value?.trim();
    if (!name) {
      if (this.#debug) console.log('>>> name check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul je naam in.' });
      return;
    }

    const email = this.#form.querySelector('[name="email"]')?.value?.trim();
    if (!email) {
      if (this.#debug) console.log('>>> email check 1 FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul een e-mailadres in.' });
      return;
    }
    else if (!this.#isEmail(email)) {
      if (this.#debug) console.log('>>> email check 2 FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul een geldig e-mailadres in.' });
      return;
    }

    const phone = this.#form.querySelector('[name="phone"]')?.value?.trim();
    if (!phone) {
      if (this.#debug) console.log('>>> phone check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul een telefoonnummer in.' });
      return;
    }

    const municipality = this.#form.querySelector('[name="municipality"]')?.value;
    if (!municipality || municipality === 'Gemeente') {
      if (this.#debug) console.log('>>> municipality check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Selecteer een gemeente.' });
      return;
    }

    const message = this.#form.querySelector('[name="message"]')?.value?.trim();
    if (!message) {
      if (this.#debug) console.log('>>> message check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul een bericht in.' });
      return;
    }

    const agreement = this.#form.querySelector('[name="agreement"]')?.checked;
    if (!agreement) {
      if (this.#debug) console.log('>>> agreement check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Accepteer de privacyverklaring om door te gaan.' });
      return;
    }

    if (this.#debug) console.log('all required fields are valid, submitting form...');

    const params = new URLSearchParams({
      d: this.#form.querySelector('[name="contact-d"]')?.value,
      subject: selectedSubject.value,
      'subject-other': selectedSubject.value === '-99' ? this.#subjectOtherInput.value.trim() : '',
      name,
      email,
      phone,
      municipality,
      message,
      agreement
    });

    if (this.#debug) console.log([...params.entries()]);

    this.#submitButton.disabled = true;
    fetch(`${cURL}mwork/ajax/handle-contact-form.jsp`, { method: 'POST', mode: 'same-origin', body: params })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then(response => {
        if (this.#debug) console.log(response);
        const message = this.#getCallbackMessage(response);
        let icon = 'error';
        if (response === 'success') {
          icon = 'success';
          this.#form.reset();
          this.#toggleSubjectOther();
        }
        else if (response === 'spam') icon = 'question';
        Swal.fire({ icon, title: '', html: message });
      })
      .catch(error => {
        if (this.#debug) console.log(`error: ${error}`);
        const message = this.#getCallbackMessage('ajaxerror');
        Swal.fire({
          icon: 'error',
          title: 'Fout: formulier niet verzonden',
          html: message
        });
      })
      .finally(() => {
        this.#submitButton.disabled = false;
      });
  }
}
